import { writable } from 'svelte/store';
import type { Writable } from 'svelte/store';
import debounce from 'lodash/debounce';
import dayjs from 'dayjs';

import { FilterFormLabels, FilterFormValues, FilterType } from './types';
import { DefaultOptionList, DefaultOptions } from '$saga/types/search.types';
import { defaultFormLabels, defaultFormValues } from './constants';

export const isSecondaryFiltersOpen = writable(false);

export const filtersContext: Writable<{
    formLabels: FilterFormLabels;
    formValues: FilterFormValues;
    selectedFilterType: FilterType;
    defaultOptions: DefaultOptionList | undefined;
    defaultDestinationOptions: DefaultOptions | undefined;
    defaultSportOptions: DefaultOptions | undefined;
    isHomePage: boolean;
    isSubmitting: boolean;
}> = writable({
    formLabels: defaultFormLabels,
    formValues: defaultFormValues,
    selectedFilterType: FilterType.SELECTION,
    defaultOptions: undefined,
    defaultDestinationOptions: undefined,
    defaultSportOptions: undefined,
    isHomePage: false,
    isSubmitting: false,
});

export const filteredToursCount = writable<number | null>(null);

export async function updateFilteredToursCount(formValues: FilterFormValues) {
    try {
        // build URL for GET request
        const queryParams = new URLSearchParams();
        Object.entries(formValues).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                if (Array.isArray(value)) {
                    // pop last 's' character from key if it ends with 's' because bad form naming in backend
                    if (key.endsWith('s')) {
                        key = key.slice(0, -1);
                    }
                    value.forEach((item) =>
                        queryParams.append(key + '[]', item),
                    );
                } else {
                    // Format DateValue objects and Date objects to DD/MM/YYYY to match backend expectations
                    let formattedValue = value?.toString() ?? '';
                    if (
                        value &&
                        typeof value === 'object' &&
                        'calendar' in value &&
                        'era' in value &&
                        'year' in value
                    ) {
                        // This is a DateValue object from @internationalized/date
                        formattedValue = dayjs(
                            `${value.year}-${value.month
                                .toString()
                                .padStart(2, '0')}-${value.day
                                .toString()
                                .padStart(2, '0')}`,
                        ).format('DD/MM/YYYY');
                    } else if (
                        value &&
                        typeof value === 'object' &&
                        'getTime' in value
                    ) {
                        // This is a regular Date object (e.g., from month selection)
                        formattedValue = dayjs(value as Date).format(
                            'DD/MM/YYYY',
                        );
                    }
                    queryParams.append(key, formattedValue);
                }
            }
        });
        const url = `/voyages/count?${queryParams.toString()}`;
        const response = await fetch(url, {
            headers: {
                'X-Count-Results': 'true',
            },
            method: 'GET',
        });

        if (response.ok) {
            const data = await response.json();
            filteredToursCount.set(data.count);
        } else {
            filteredToursCount.set(null);
        }
    } catch (e) {
        filteredToursCount.set(null);
    }
}

let prevFormValues: FilterFormValues | null = null;
let isFirstRun = true;

const debouncedUpdateFilteredToursCount = debounce((formValues) => {
    // Only call if values actually changed
    if (JSON.stringify(formValues) !== JSON.stringify(prevFormValues)) {
        prevFormValues = JSON.parse(JSON.stringify(formValues));
        updateFilteredToursCount(formValues);
    }
}, 300);

// Subscribe to formValues changes and update count
defaultFormValues &&
    filtersContext.subscribe(($ctx) => {
        if (isFirstRun) {
            isFirstRun = false;
            prevFormValues = JSON.parse(JSON.stringify($ctx.formValues));
            return;
        }
        debouncedUpdateFilteredToursCount($ctx.formValues);
    });
